import { Card } from "@/components/Card";
import type { OpenIntakeDraft } from "@/hooks/useIntakeDrafts";

// Surfaced when the signed-in patient has at least one in-flight intake
// that isn't complete. Common case: they're in the portal for TRT but
// started a WL intake and abandoned it (or vice versa).

const INTAKE_HOSTS: Record<"wl" | "trt", string> = {
  wl: "https://intake.fortehealth.co",
  trt: "https://trt.fortehealth.co",
};

const LABELS: Record<"wl" | "trt", { name: string; cta: string }> = {
  wl:  { name: "Weight Loss",   cta: "Resume weight loss assessment" },
  trt: { name: "Testosterone",  cta: "Resume testosterone assessment" },
};

function fmtRelative(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 60) return `${Math.max(1, mins)} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function IntakeResumeCard({ drafts }: { drafts: OpenIntakeDraft[] }) {
  if (drafts.length === 0) return null;

  return (
    <Card
      title="Finish your assessment"
      action={
        <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider bg-fh-sand text-fh-green-dark">
          In progress
        </span>
      }
    >
      <ul className="divide-y divide-fh-border -my-1">
        {drafts.map((d) => {
          const label = LABELS[d.treatment_type as "wl" | "trt"];
          const host = INTAKE_HOSTS[d.treatment_type as "wl" | "trt"];
          const url = `${host}/?resume=${encodeURIComponent(d.resume_token)}`;
          return (
            <li key={d.id} className="py-3 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-[14px] font-semibold text-fh-ink">{label.name} assessment</div>
                <div className="text-[12.5px] text-fh-gray">
                  Started {fmtRelative(d.started_at)} · last activity {fmtRelative(d.last_progress_at)}
                </div>
              </div>
              <a
                href={url}
                className="shrink-0 inline-flex items-center justify-center px-3 py-2 rounded-[10px] bg-fh-green-dark text-white text-[13px] font-semibold hover:bg-fh-green transition focus:outline-none focus-visible:ring-2 focus-visible:ring-fh-green focus-visible:ring-offset-2 focus-visible:ring-offset-white"
              >
                {label.cta}
              </a>
            </li>
          );
        })}
      </ul>
      <p className="text-[12px] text-fh-gray mt-3">
        Your answers are saved. Picking up where you left off takes about five minutes.
      </p>
    </Card>
  );
}
